import {
  addUsersToTeam,
  getRestAccountTeams,
  getRestMySecurityGroups,
  removeUsersFromTeam,
} from "api/teamStore";
import { TeamState } from "modules/rtk/team/teamTypes";
import {
  CxSecurityGroupDetailsDto,
  MySecurityGroupsResponse,
} from "lib/ShiOneClient";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "store";
import GlobalConstants from "@constants";

import _ from "lodash";

const initialState: TeamState = {
  teams: [],
  teamsFetchStatus: GlobalConstants.fetchStatus.idle,
  addUserToTeamStatus: GlobalConstants.fetchStatus.idle,
  removeUserFromTeamStatus: GlobalConstants.fetchStatus.idle,
  updatingTeamStatus: "",
  myTeams: [],
  myTeamsFetchStatus: GlobalConstants.fetchStatus.idle,
  isSecurityGroupsEnabled: false,
  isGlobalAdmin: false,
};

const teamSlice = createSlice({
  name: "teamRtk",
  initialState,
  reducers: {
    receivingAccountTeams(state) {
      state.teamsFetchStatus = GlobalConstants.fetchStatus.loading;
    },
    receiveAccountTeams(state, action: PayloadAction<CxSecurityGroupDetailsDto[]>) {
      state.teamsFetchStatus = GlobalConstants.fetchStatus.complete;
      state.teams = action.payload;
    },
    receiveAccountTeamsError(state) {
      state.teamsFetchStatus = GlobalConstants.fetchStatus.error;
    },
    addingUserToTeam(state) {
      state.addUserToTeamStatus = GlobalConstants.fetchStatus.loading;
    },
    addedUserToTeam(state, action: PayloadAction<CxSecurityGroupDetailsDto>) {
      state.addUserToTeamStatus = GlobalConstants.fetchStatus.complete;
      const newTeamsContacts = _.map(state.teams, (team) => {
        if (team.id === action.payload.id) {
          return action.payload;
        }
        return team;
      });
      state.teams = newTeamsContacts;
    },
    addUserToTeamError(state) {
      state.addUserToTeamStatus = GlobalConstants.fetchStatus.error;
    },
    removingUserFromTeam(state) {
      state.removeUserFromTeamStatus = GlobalConstants.fetchStatus.loading;
    },
    removedUserFromTeam(state, action: PayloadAction<CxSecurityGroupDetailsDto>) {
      state.removeUserFromTeamStatus = GlobalConstants.fetchStatus.complete;
      const newTeamsRemoveContacts = _.map(state.teams, (team) => {
        if (team.id === action.payload.id) {
          return action.payload;
        }
        return team;
      });
      state.teams = newTeamsRemoveContacts;
    },
    removeUserFromTeamError(state) {
      state.removeUserFromTeamStatus = GlobalConstants.fetchStatus.error;
    },
    requestMyTeams(state) {
      state.myTeamsFetchStatus = GlobalConstants.fetchStatus.loading;
    },
    receivedMyTeams(state, action: PayloadAction<MySecurityGroupsResponse>) {
      state.myTeams = action.payload.securityGroups ?? [];
      state.isSecurityGroupsEnabled = action.payload.isSecurityGroupsEnabled ?? false;
      state.isGlobalAdmin = action.payload.isGlobalAdmin ?? false;
      state.myTeamsFetchStatus = GlobalConstants.fetchStatus.complete;
    },
    receiveMyTeamsError(state) {
      state.myTeamsFetchStatus = GlobalConstants.fetchStatus.error;
    },
  },
});

export const getAccountTeams =
  (): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(teamSlice.actions.receivingAccountTeams());
    try {
      const accountTeams = await getRestAccountTeams();

      dispatch(teamSlice.actions.receiveAccountTeams(accountTeams));
    } catch {
      dispatch(teamSlice.actions.receiveAccountTeamsError());
    }
  };

export const addContactsToTeams =
  (teamId: number, userIds: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(teamSlice.actions.addingUserToTeam());
    try {
      const team = await addUsersToTeam(teamId, userIds);

      dispatch(teamSlice.actions.addedUserToTeam(team));
    } catch {
      dispatch(teamSlice.actions.addUserToTeamError());
    }
  };

export const removeContactsFromTeams =
  (teamId: number, userIds: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(teamSlice.actions.removingUserFromTeam());
    try {
      const team = await removeUsersFromTeam(teamId, userIds);

      dispatch(teamSlice.actions.removedUserFromTeam(team));
    } catch {
      dispatch(teamSlice.actions.removeUserFromTeamError());
    }
  };

export const getMyTeams = (): AppThunk => async (dispatch: AppDispatch) => {
  dispatch(teamSlice.actions.requestMyTeams());
  try {
    const myTeams = await getRestMySecurityGroups();

    dispatch(teamSlice.actions.receivedMyTeams(myTeams));
  } catch {
    dispatch(teamSlice.actions.receiveMyTeamsError());
  }
};

export default teamSlice.reducer;

